import React, { useState, useEffect } from "react";

const STATUS_CONFIG = {
  pending:     { color: 'var(--st-pending)',     label: 'Pending' },
  pitched:     { color: 'var(--st-pitched)',     label: 'Pitched' },
  engaged:     { color: 'var(--st-engaged)',     label: 'Engaged' },
  confirmed:   { color: 'var(--st-confirmed)',   label: 'Confirmed' },
  no_interest: { color: 'var(--st-no-interest)', label: 'No interest' },
};

const PILL_LABELS = { topfit: 'Not pitched', warm: 'Warm leads', reeng: 'Re-engageable' };

function StatusChip({ status }) {
  const c = STATUS_CONFIG[status];
  if (!c) return <span style={{ fontSize: 12, color: 'var(--text-5)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span className="r-dot" style={{ color: c.color }} />
      <span style={{ fontSize: 12, fontWeight: 500, color: c.color }}>{c.label}</span>
    </span>
  );
}

export default function ExpertsView({ API, stats }) {
  const [experts, setExperts] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expertFilter, setExpertFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('expertnet_token');
    fetch(`${API}/experts/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setExperts)
      .catch(() => {});
  }, [API]);

  const projectOptions = [...new Set(experts.map(e => e.project_name))].sort();

  const filtered = experts.filter(e => {
    if (filterProject && e.project_name !== filterProject) return false;
    if (filterStatus && e.status !== filterStatus) return false;
    if (expertFilter === 'topfit') return e.status === 'pending';
    if (expertFilter === 'warm') return e.status === 'engaged' || e.status === 'pitched';
    if (expertFilter === 'reeng') return e.status === 'no_interest';
    return true;
  });

  const pipelineCounts = {
    topfit_unpitched: experts.filter(e => e.status === 'pending').length,
    warm_leads: experts.filter(e => e.status === 'engaged' || e.status === 'pitched').length,
    reengageable: experts.filter(e => e.status === 'no_interest').length,
  };
  const COLS = 'minmax(160px,1.2fr) 1.4fr 1.3fr 120px 1.2fr';

  return (
    <div style={{ padding: 24, maxWidth: 1440, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Rasp AI banner */}
      <div style={{ borderRadius: 12, border: '1px solid rgba(45,212,191,.35)', background: 'linear-gradient(90deg, rgba(14,116,144,.38), rgba(45,212,191,.10) 45%, rgba(10,22,40,.6))', backdropFilter: 'blur(18px)', padding: '14px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 auto' }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,#0e7490,#2dd4bf)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'inline-block', animation: 'rasp-pulse 2.4s ease-in-out infinite' }} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="r-eyebrow" style={{ color: 'var(--accent)' }}>Expert intelligence</span>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--positive)', display: 'inline-block' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-4)' }}>LIVE</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>Rasp AI</div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginLeft: 8 }}>
            <span style={{ fontFamily: 'var(--font-data)' }}>{pipelineCounts.topfit_unpitched}</span> experts haven't been pitched yet · <span style={{ fontFamily: 'var(--font-data)' }}>{pipelineCounts.warm_leads}</span> warm leads · <span style={{ fontFamily: 'var(--font-data)' }}>{pipelineCounts.reengageable}</span> marked no interest.
          </div>
        </div>
        <button className="r-btn r-btn-primary" style={{ height: 28, fontSize: 12, flexShrink: 0 }} onClick={() => setExpertFilter('topfit')}>Show not pitched</button>
      </div>

      {/* Stats strip */}
      <div className="r-card" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: 'var(--line)', overflow: 'hidden' }}>
        {[
          { label: 'TOTAL EXPERTS', value: experts.length,                                              color: 'var(--text-1)' },
          { label: 'PITCHED',       value: experts.filter(e => e.status === 'pitched').length,          color: 'var(--st-pitched)' },
          { label: 'ENGAGED',       value: experts.filter(e => e.status === 'engaged').length,          color: 'var(--st-engaged)' },
          { label: 'CONFIRMED',     value: experts.filter(e => e.status === 'confirmed').length,        color: 'var(--st-confirmed)' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--glass-card)', padding: '16px 20px' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 500, color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.value.toLocaleString()}</div>
            <div className="r-eyebrow" style={{ marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { id: 'all',    label: `All experts · ${experts.length}`,                         dot: false },
          { id: 'topfit', label: `Not pitched · ${pipelineCounts.topfit_unpitched}`,         dot: true },
          { id: 'warm',   label: `Warm leads · ${pipelineCounts.warm_leads}`,                dot: true },
          { id: 'reeng',  label: `Re-engageable · ${pipelineCounts.reengageable}`,           dot: true },
        ].map(f => {
          const active = expertFilter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setExpertFilter(f.id)}
              style={{ height: 28, padding: '0 12px', borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, background: active ? 'rgba(45,212,191,.16)' : 'transparent', border: active ? '1px solid rgba(45,212,191,.4)' : '1px solid var(--line)', color: active ? 'var(--text-1)' : 'var(--text-3)' }}
            >
              {f.dot && <span className="r-dot" style={{ color: 'var(--accent)' }} />}
              {f.label}
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <select
            value={filterProject}
            onChange={e => setFilterProject(e.target.value)}
            style={{ height: 28, background: 'var(--input-bg)', border: '1px solid var(--line)', borderRadius: 8, fontSize: 12, color: 'var(--text-2)', padding: '0 8px', colorScheme: 'dark', cursor: 'pointer' }}
          >
            <option value="">All projects</option>
            {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ height: 28, background: 'var(--input-bg)', border: '1px solid var(--line)', borderRadius: 8, fontSize: 12, color: 'var(--text-2)', padding: '0 8px', colorScheme: 'dark', cursor: 'pointer' }}
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {/* Expert table */}
      <div className="r-card" style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 900 }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '0 18px', height: 32, alignItems: 'center', background: 'var(--subbar-bg)', borderBottom: '1px solid var(--line)', fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-4)' }}>
            <span>Name</span>
            <span>Title / Company</span>
            <span>Email</span>
            <span>Status</span>
            <span>Project</span>
          </div>

          {/* Rows */}
          {filtered.length === 0 ? (
            <div style={{ color: 'var(--text-4)', textAlign: 'center', padding: '40px 0', fontSize: 13 }}>No experts found</div>
          ) : filtered.map((expert, i) => (
            <div
              key={expert.pe_id}
              style={{ display: 'grid', gridTemplateColumns: COLS, padding: '0 18px', minHeight: 48, alignItems: 'center', borderBottom: '1px solid var(--line-soft)', background: i % 2 === 1 ? 'var(--row-odd)' : 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 1 ? 'var(--row-odd)' : 'transparent'}
            >
              <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-1)' }}>{expert.name}</span>
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{expert.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{expert.company}</div>
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: expert.email ? 'var(--accent)' : 'var(--text-5)' }}>{expert.email || '—'}</span>
              <span><StatusChip status={expert.status} /></span>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{expert.project_name}</span>
            </div>
          ))}

          {/* Footer */}
          <div style={{ height: 40, padding: '0 18px', display: 'flex', alignItems: 'center', borderTop: '1px solid var(--line-soft)', fontSize: 12, color: 'var(--text-3)' }}>
            {filtered.length} of {experts.length} experts{expertFilter !== 'all' && ` · ${PILL_LABELS[expertFilter]}`}
          </div>
        </div>
      </div>
    </div>
  );
}
